import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { TableDetailsResponseDto } from '../../services/tables/dto/table-details-dto';
import { ApplyVoucherDto, ApplyVoucherResponseDto, RemoveVoucherDto, TableApiService } from '../../services/tables/table-api.service';
import { Payment } from '../models/payment';
import { Table } from '../models/table';
import { TableItem } from '../models/table-item';
import { PaymentDone } from '../models/payment-done';
import { UserService } from './user.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { TableItemOption } from '../models/table-item-option';

export type CloseType = 'TOTAL' | 'PARTIAL' | 'CUSTOM';
export type ReceiptType = 'ALL' | 'USER';

export interface PaymentMethodDiscount {
  type: 'PERCENTAGE' | 'FIXED';
  value: number;
}

@Injectable({
  providedIn: 'root'
})
export class TableService {

  openedTable: Table;
  $openedTable: BehaviorSubject<Table | null> = new BehaviorSubject<Table | null>(null)
  $tableClosed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  $paymentMethodDiscount: BehaviorSubject<PaymentMethodDiscount | null> = new BehaviorSubject<PaymentMethodDiscount | null>(null)
  selectedPayment: Payment;

  private menuUrl?: string
  private moduleStepAuth?: string
  private lastLoadTimestamp: number = 0;
  private readonly RELOAD_THRESHOLD_MS = 10000; // 10 seconds before allowing reload

  constructor(
    private tableApiService: TableApiService,
    private userService: UserService,
    private localStorageService: LocalStorageService
  ) {
     // Quando o usuário faz login, recarrega a mesa para obter dados de fidelidade
     this.userService.$authState.subscribe((authState) => {
      if (authState === 'AUTHORIZED' && this.openedTable) {
        const tableUuid = this.localStorageService.getItem<string>('table_uuid');
        if (tableUuid) {
          this.loadTable(tableUuid, true); // forceReload para obter fidelity do backend
        }
      }
    });
  }

  loadTable(uuid: string, forceReload: boolean = false): void {
    // Check if we should skip loading if data is recent
    if (!forceReload && this.shouldSkipReload()) {
      return;
    }

    // zera flag stale antes de recarregar: um "closed" antigo (ex: reload
    // forçado por socket que falhou) não pode redirecionar um mount novo
    this.$tableClosed.next(false);

    const cartUuid = this.localStorageService.getItem<string>('cart_uuid') ?? undefined;
    this.tableApiService.getTableData(uuid, cartUuid).subscribe({
      next: (response: TableDetailsResponseDto) => {
        if(response.data) {
          this.menuUrl = response.data.menuUrl;
          this.moduleStepAuth = response.data.moduleStepAuth;

          // Save config to localStorage if not present
          this.localStorageService.setItem('config', response.data.config);

          // Save produtoImpressao to localStorage if not present
          this.localStorageService.setItem('produtoImpressao', response.data.produtoImpressao);

          const prev = this.openedTable;
          this.openedTable = new Table(response.data)
          this.preserveClientState(prev, this.openedTable);
          // Salva o cart_uuid com expiração de 2 horas (tempo de sessão da mesa)
          this.localStorageService.setItem('cart_uuid', this.openedTable.tableSession, LocalStorageService.timeToMs.hours(24));
          this.lastLoadTimestamp = Date.now();
          this.$openedTable.next(this.openedTable);
          this.$tableClosed.next(false);
        } else {
          // Mesa não encontrada ou desabilitada
          this.$openedTable.next(null);
          this.$tableClosed.next(true);
        }
      },
      error: (error) => {
        console.log('❌ Erro ao carregar mesa:', error);
        this.$openedTable.next(null);
        this.$tableClosed.next(true);
      }
    });
  }

  private shouldSkipReload(): boolean {
    if (!this.openedTable) {
      return false; // Always load if no table exists
    }
    const now = Date.now();
    const timeSinceLastLoad = now - this.lastLoadTimestamp;
    return timeSinceLastLoad < this.RELOAD_THRESHOLD_MS;
  }

  /**
   * Copia o estado de cliente (paymentType, customPayment, removeTax, receiptType e
   * qtySelected por item) do Table antigo pro novo. O construtor de Table sempre
   * inicializa esses campos com defaults (paymentType='TOTAL', qtySelected=0), então
   * sem isso qualquer loadTable disparado no meio do fluxo (cashback toggle, voucher,
   * login, etc.) faz o usuário sair de PARTIAL/CUSTOM e perder a seleção.
   */
  private preserveClientState(prev: Table | undefined, next: Table): void {
    if (!prev) return;
    if (prev.paymentType) next.paymentType = prev.paymentType;
    if (prev.customPayment != null) next.customPayment = prev.customPayment;
    if (prev.removeTax !== undefined) next.removeTax = prev.removeTax;
    if (prev.receiptType) next.receiptType = prev.receiptType;

    if (prev.items?.length && next.items?.length) {
      const selectionById = new Map<number, number>();
      for (const item of prev.items) {
        if (item.qtySelected > 0) selectionById.set(item.id, item.qtySelected);
      }
      for (const item of next.items) {
        const sel = selectionById.get(item.id);
        if (sel != null) {
          // a seleção guardada é do item agrupado; a linha crua tem qty 1,
          // então o clamp precisa ser o tamanho do grupo
          const max = item.type === 'WEIGHTABLE'
            ? item.qty
            : next.items.filter((i) => this.sameGroup(i, item)).length;
          item.qtySelected = Math.min(sel, max);
        }
      }
    }
  }

  hasValidTableData(): boolean {
    return this.openedTable !== null && this.openedTable !== undefined;
  }

  getMenuUrl(): string | undefined {
    return this.menuUrl;
  }

  getTable(): Table {
    return this.openedTable;
  }

  getModuleStepAuth(): string | undefined {
    return this.moduleStepAuth;
  }

  setPayment(payment: Payment) {
    this.selectedPayment = payment;
  }

  getPayment(): Payment {
    return this.selectedPayment;
  }

  setPaymenType(closeType: CloseType): void {
    this.openedTable.paymentType = closeType;

    if(closeType == 'PARTIAL') {
      this.preSelectOwnedItems();
    }
  }

  /** Marca os itens do próprio usuário, só quando nada está selecionado. */
  private preSelectOwnedItems(): void {
    const table = this.tableWithGroupedItems();
    if(!table || table.qtyItensSelected > 0) return;

    for(const item of table.items) {
      if(item.qtyOwned > 0) {
        item.qtySelected = item.qtyOwned;
      }
    }
  }

  setReceiptType(type: ReceiptType): void {
    this.openedTable.receiptType = type;
  }

  getReceiptType(): ReceiptType | undefined {
    return this.openedTable.receiptType;
  }

  setRemoveTax(paid: boolean) {
    this.openedTable.removeTax = paid;
  }

  getRemoveTax(): boolean {
    return this.openedTable.removeTax ?? false;
  }

  setCustomPayment(customPayment: number) {
    this.openedTable.customPayment = customPayment;
  }

  getReceipts() {
    const user = this.userService.getUser();
    if(this.getTable().receiptType == 'USER' && user) {
      return this.getTable().payments.filter((payment: PaymentDone) => payment.userId == user.id);
    } else {
      this.setReceiptType('ALL');
      return this.getTable().payments;
    }
  }

  tableAlreadyPaid() {
    return this.getTable().tableAlreadyPaid;
  }

  increaseTotalItemSelected(item: TableItem,qty :number) {
    const index = this.openedTable.items.findIndex((i: TableItem) => i.id == item.id);

    if(index >= 0) {
      if(item.type == 'WEIGHTABLE') {
        this.openedTable.items[index].qtySelected++;
      } else {
        if(!this.openedTable.items[index].qtySelected) {
          this.openedTable.items[index].qtySelected = qty;
        } else {
          this.openedTable.items[index].qtySelected++
        }
      }
    }
  }

  decreaseTotalItemSelected(item: TableItem, remove: boolean = false) {
    const index = this.openedTable.items.findIndex((i: TableItem) => i.id == item.id);

    if(index >= 0) {
      if(item.type == 'WEIGHTABLE') {
        this.openedTable.items[index].qtySelected = 0;
      } else {
        if(remove || this.openedTable.items[index].qtySelected == 1) {
          this.openedTable.items[index].qtySelected = 0;
        } else {
          this.openedTable.items[index].qtySelected--
        }
      }
    }
  }

  clearSession(): void {
    this.localStorageService.removeItem('table_uuid');
    this.localStorageService.removeItem('cart_uuid');
    this.localStorageService.removeItem('table_id');
    this.localStorageService.removeItem('table_cart_id');
    this.localStorageService.removeItem('intent');
    this.localStorageService.removeItem('intent_total');
  }

  tableWithGroupedItems(): Table | null {
    const table = this.getTable();
    if (!table) return null;

    // Idempotente: se já agrupou nesta openedTable, devolve direto. O Table
    // constructor inicializa groupedItemsId = [] em items frescos, então
    // length > 0 só após o agrupamento abaixo. loadTable cria new Table
    // (items frescos), naturalmente re-agrupando no próximo subscribe.
    if (table.items.some(item => item.groupedItemsId?.length > 0)) {
      return table;
    }

    // Muta openedTable.items in-place: increaseTotalItemSelected/
    // decreaseTotalItemSelected procuram por id em openedTable.items, então
    // o template e o increase precisam apontar pro mesmo array. Itens
    // duplicados são consolidados num "representante" (1º id do grupo).
    // payment.service expande de volta via item.groupedItemsId no PARTIAL.
    const userId = this.userService.getUser()?.id;
    const groupedItems: Array<TableItem> = [];
    for (const item of table.items) {
      const index = groupedItems.findIndex((produto) => this.sameGroup(produto, item));
      if (index >= 0 && item.type !== 'WEIGHTABLE') {
        groupedItems[index].qtyGrouped++;
        groupedItems[index].valueGrouped += groupedItems[index].value;
        groupedItems[index].groupedItemsId.push(item.id);
        groupedItems[index].qtyOwned += this.ownedQty(item, userId);
      } else {
        item.qtyGrouped = item.type === 'WEIGHTABLE' ? item.qty : 1;
        item.valueGrouped = item.value;
        item.groupedItemsId = [item.id];
        item.qtyOwned = this.ownedQty(item, userId);
        groupedItems.push(item);
      }
    }

    table.items = groupedItems;
    return table;
  }

  /** dois itens caem na mesma linha agrupada */
  private sameGroup(produto: TableItem, item: TableItem): boolean {
    let comsposicoesIguais = true;
    if(item.options.length != produto.options.length){
      comsposicoesIguais = false
    }
    produto.options.forEach((composicao: TableItemOption) => {
      const idx = item.options.findIndex((composition_item: TableItemOption) =>
        composition_item.name == composicao.name &&
        composition_item.qty == composicao.qty &&
        composition_item.value == composicao.value
      );
      if(idx == -1){
        comsposicoesIguais = false
      }
    })
    return (
      // itens iguais de pessoas diferentes não podem virar uma linha só
      produto.userId === item.userId &&
      produto.name === item.name &&
      produto.additionalInfo === item.additionalInfo &&
      produto.qty === item.qty &&
      produto.type === item.type &&
      produto.status === item.status &&
      produto.value === item.value &&
      comsposicoesIguais
    );
  }

  /** quanto do item é do usuário logado; a taxa nunca tem dono */
  private ownedQty(item: TableItem, userId?: number): number {
    if(!userId || item.tip || item.userId !== userId) return 0;

    return item.type === 'WEIGHTABLE' ? item.qty : 1;
  }

  setVoucher(userId: number, voucherCode: string): Observable<ApplyVoucherResponseDto> {
    const dto: ApplyVoucherDto = {
      idi: this.openedTable.idi,
      cartId: this.openedTable.tableCartId,
      cartTotal: this.openedTable.total,
      userId: userId,
      voucherCode: voucherCode,
    };

    console.log('Aplicando cupom...', dto);

    return this.tableApiService.applyVoucher(dto).pipe(
      tap((response: ApplyVoucherResponseDto) => {
        // Atualiza a mesa com o voucher aplicado
        this.openedTable.vouchers = [{
          id: response.id,
          voucherId: response.voucherId,
          voucherName: response.voucherName,
          discountType: response.discountType,
          discountValue: response.discountValue,
          discountValueApplied: response.discountValueApplied,
        }];
        // Recarrega a mesa pra puxar remaining/subtotal/tableFinalValue já com cupom abatido pelo back.
        const tableUuid = this.localStorageService.getItem<string>('table_uuid');
        if (tableUuid) {
          this.loadTable(tableUuid, true);
        } else {
          this.$openedTable.next(this.openedTable);
        }
      })
    );
  }

  removeVoucher(voucherId: number, userId?: number): Observable<any> {
    const dto: RemoveVoucherDto = {
      idi: this.openedTable.idi,
      cartId: this.openedTable.tableCartId,
      voucherId: voucherId,
      userId: userId,
    };

    console.log('Removendo cupom...', dto);

    return this.tableApiService.removeVoucher(dto).pipe(
      tap(() => {
        // Remove o voucher da mesa
        this.openedTable.vouchers = [];
        // Recarrega a mesa pra puxar remaining/subtotal/tableFinalValue sem o cupom.
        const tableUuid = this.localStorageService.getItem<string>('table_uuid');
        if (tableUuid) {
          this.loadTable(tableUuid, true);
        } else {
          this.$openedTable.next(this.openedTable);
        }
      })
    );
  }

  setPaymentMethodDiscount(discount: PaymentMethodDiscount | null): void {
    this.$paymentMethodDiscount.next(discount);
  }

  getPaymentMethodDiscount(): PaymentMethodDiscount | null {
    return this.$paymentMethodDiscount.getValue();
  }
}
